/**
 * Tunables. Every magic number that more than one subsystem cares about lives
 * here, so that a balance or timing change is a data edit rather than a hunt.
 */

/** Simulation ticks per second. Fixed; the simulation never reads a clock. */
export const TICK_HZ = 30
export const TICK_MS = 1000 / TICK_HZ

/**
 * Ticks between a command being submitted and executed. Fixed at 3 (100 ms) per
 * KTD4 — an adaptive controller inside the tick barrier satisfies no stated
 * requirement and would be tuned against guesses until U18 produces real
 * latency data.
 */
export const INPUT_DELAY_TICKS = 3

/** The relay will not accept a command scheduled further ahead than this. */
export const MAX_INPUT_DELAY_TICKS = 12

/** Ticks between state-hash checkpoints. */
export const HASH_INTERVAL = 30

/** Map is square, this many cells on a side. */
export const MAP_SIZE = 128

/** Pathfinding cluster edge, in cells. MAP_SIZE must be a multiple of it. */
export const CLUSTER_SIZE = 16

/** Hard entity ceiling. Slot indices are Uint16, so this must stay under 65535. */
export const MAX_ENTITIES = 4096

/** Players per match. */
export const MAX_PLAYERS = 4

/** Commands referencing more entities than this are rejected outright (R35). */
export const MAX_COMMAND_UNITS = 256

/** Commands accepted from one player in one tick (R35). */
export const MAX_COMMANDS_PER_TICK = 32

/** Bytes accepted in one network frame (R35). */
export const MAX_MESSAGE_BYTES = 8192

/** Items a single sidebar tab may hold, matching Red Alert 2. */
export const MAX_QUEUE_DEPTH = 30

/**
 * Ticks after which an undecided match is reported as a stall — distinctly from
 * a win and distinctly from a draw (R30). Provisional; tuned in U20 against
 * real bot-match durations.
 */
export const MATCH_TICK_CAP = 30 * 60 * 45

/**
 * Reference internal render target. The pixel *density* is fixed — that is what
 * KTD2 requires and what keeps the dither from crawling — but the **extent**
 * follows the window, so the world fills the screen instead of sitting in a
 * letterbox. These two are the fallback size before the first layout, and the
 * reference the camera basis is built against.
 */
export const INTERNAL_WIDTH = 480
export const INTERNAL_HEIGHT = 270

/**
 * How many display pixels one internal pixel occupies. At 1 the render is
 * native; above 1 it is a chunky upscale. It must be a whole number, or a
 * source pixel straddles two destination pixels and the grid dissolves.
 */
export const PIXEL_SCALE = 1

/**
 * Internal pixels per world cell — the zoom, held constant so that a bigger
 * window shows *more map* rather than a more magnified one.
 *
 * With `PIXEL_SCALE` this fixes both how large a unit is on screen and how many
 * pixels it is drawn from. 48 native pixels per cell keeps a cell the same size
 * on screen as the earlier chunky 24×2 while drawing it from four times the
 * pixels; past about 60 the view is too zoomed in to command from.
 */
export const PIXELS_PER_CELL = 48

/**
 * How far from an owned structure a new one may be placed, in cells: the
 * footprint must come within this Chebyshev distance of a cell an owned
 * structure occupies, so up to `BUILD_RADIUS - 1` empty cells may separate the
 * two.
 *
 * Not strict adjacency: Red Alert lets a base breathe, and a rule that demands
 * every building touch its neighbour produces a solid block rather than a base
 * with lanes through it. Four cells is enough to leave a road between rows and
 * to step a base forward deliberately, and short enough that creeping across the
 * map takes visible time and buildings to do.
 */
export const BUILD_RADIUS = 4

/** Palette entry count the quantizer snaps to. */
export const PALETTE_SIZE = 72

/** Simultaneous audio voices before the quietest is dropped. */
export const VOICE_BUDGET = 24

export interface QualityPreset {
  readonly name: string
  readonly shadows: boolean
  readonly dither: boolean
  readonly maxParticles: number
}

export const QUALITY_PRESETS: Record<string, QualityPreset> = {
  low: { name: 'low', shadows: false, dither: true, maxParticles: 128 },
  medium: { name: 'medium', shadows: true, dither: true, maxParticles: 512 },
  high: { name: 'high', shadows: true, dither: true, maxParticles: 2048 },
}

export interface Config {
  quality: QualityPreset
  /**
   * Snap colour to the generated palette with ordered dithering.
   *
   * Off by default. The dither existed to fake extra shades out of 48 colours,
   * and read as a haze of dots over everything; without the size constraint it
   * buys nothing. Kept as a switch because it is the one honest way to see
   * whether a colour defect is in the shading or in the quantizer.
   */
  quantize: boolean
  /** Display pixels per internal pixel. See `PIXEL_SCALE`. */
  pixelScale: number
}

export function defaultConfig(): Config {
  return {
    quality: QUALITY_PRESETS.medium,
    quantize: false,
    pixelScale: PIXEL_SCALE,
  }
}
